import { usePermission } from "../../components/permission";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  CopyButton,
  Drawer,
  Flex,
  Group,
  Modal,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  ArrowUUpLeftIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CheckIcon,
  CopyIcon,
  WarningCircleIcon,
  WarningIcon,
  XIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { IJournalEntry, IJournalEntryDetail } from "../../apis/ledger";
import { ModalFooter } from "../../components/ui/app-modal";
import modalChrome from "../../components/ui/app-modal.module.css";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import { SourcePill, useTemplateLabel } from "./journal-shared";
import classes from "./journal.module.css";

type Props = {
  entryId: string | null;
  /** Жагсаалтын одоогийн дарааллаар бичилтүүдийн id — ‹ › ба ← → үүгээр шилжинэ. */
  orderedIds: string[];
  /** Жагсаалтын огнооны хязгаар — дансны хуулга руу холбохдоо ашиглана. */
  rangeHint: [string | null, string | null];
  /** Баталгаажуулах цонх нээлттэй үед товчлуурыг хаана. */
  suspendKeys?: boolean;
  onClose: () => void;
  onNavigate: (id: string) => void;
  onRequestReverse: () => void;
};

const money = (v: number) => currencyFormat(Number(v || 0)).replace(/\s*₮$/, "");

function isTyping(el: Element | null) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (el as HTMLElement).isContentEditable;
}

function EntryHeader({
  entry,
  onClose,
  onPrev,
  onNext,
}: {
  entry: IJournalEntryDetail;
  onClose: () => void;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
}) {
  const { t } = useTranslation(["billing", "common"]);
  const reversed = entry.status === "REVERSED";

  return (
    <Box className={classes.drawerHeader}>
      <Flex align="center" justify="space-between" gap="sm">
        <Flex align="center" gap="xs" wrap="wrap" miw={0}>
          <Text span className={classes.drawerEntryNumber} ff="monospace">
            {entry.entryNumber}
          </Text>
          <CopyButton value={entry.entryNumber} timeout={1500}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? t("billing:ledger.copyDone") : t("billing:ledger.copy")} withArrow>
                <ActionIcon
                  variant="subtle"
                  color={copied ? "green" : "gray"}
                  size="sm"
                  radius="md"
                  onClick={copy}
                  aria-label={t("billing:ledger.copy")}
                >
                  {copied ? <CheckIcon size={14} weight="bold" /> : <CopyIcon size={14} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
          <Text span className={`${classes.pill} ${reversed ? classes.pillReversed : classes.pillPosted}`}>
            {t(`billing:ledger.status.${entry.status}`, { defaultValue: entry.status })}
          </Text>
        </Flex>
        <Group gap={4} wrap="nowrap" className={classes.drawerNav}>
          <Tooltip label={`${t("billing:ledger.prevEntry")} (←)`} withArrow>
            <ActionIcon
              variant="default"
              size="lg"
              radius="md"
              disabled={!onPrev}
              onClick={() => onPrev?.()}
              aria-label={t("billing:ledger.prevEntry")}
            >
              <CaretLeftIcon size={16} weight="bold" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={`${t("billing:ledger.nextEntry")} (→)`} withArrow>
            <ActionIcon
              variant="default"
              size="lg"
              radius="md"
              disabled={!onNext}
              onClick={() => onNext?.()}
              aria-label={t("billing:ledger.nextEntry")}
            >
              <CaretRightIcon size={16} weight="bold" />
            </ActionIcon>
          </Tooltip>
          <ActionIcon variant="subtle" color="gray" size="lg" radius="md" onClick={onClose} aria-label={t("common:close")}>
            <XIcon size={16} weight="bold" />
          </ActionIcon>
        </Group>
      </Flex>
      <Text className={classes.drawerSubline} mt={6}>
        {[dateFormat(entry.entryDate), entry.description?.trim()].filter(Boolean).join(" · ")}
      </Text>
    </Box>
  );
}

function EntrySummary({
  entry,
  onNavigate,
}: {
  entry: IJournalEntryDetail;
  onNavigate: (id: string) => void;
}) {
  const { t } = useTranslation(["billing", "common"]);
  const templateLabel = useTemplateLabel();
  const templateName = templateLabel(entry.postingTemplate);
  const invoiceId = entry.source?.invoiceId;

  return (
    <Stack gap="md">
      <Box component="dl" className={classes.dl}>
        <Box className={classes.dlItem}>
          <Box component="dt" className={classes.dt}>{t("billing:ledger.drawer.source")}</Box>
          <Box component="dd" className={classes.dd}>
            <SourcePill code={entry.sourceType} />
            {invoiceId && (
              <Link className={classes.linkBtn} to={`/sales-invoice/${invoiceId}/details`}>
                {entry.source?.refCode ?? t("billing:ledger.drawer.openInvoice")}
              </Link>
            )}
          </Box>
        </Box>
        <Box className={classes.dlItem}>
          <Box component="dt" className={classes.dt}>{t("billing:ledger.drawer.template")}</Box>
          <Box component="dd" className={classes.dd}>
            <Tooltip label={entry.postingTemplate ?? ""} withArrow disabled={!entry.postingTemplate}>
              <Text span size="sm">{templateName ?? t("common:list.dash")}</Text>
            </Tooltip>
          </Box>
        </Box>
        <Box className={classes.dlItem}>
          <Box component="dt" className={classes.dt}>{t("billing:ledger.colDate")}</Box>
          <Box component="dd" className={classes.dd}>{dateFormat(entry.entryDate)}</Box>
        </Box>
        <Box className={classes.dlItem}>
          <Box component="dt" className={classes.dt}>{t("billing:ledger.drawer.totalAmount")}</Box>
          <Box component="dd" className={`${classes.dd} ${classes.ddTotal}`}>{currencyFormat(entry.totalDebit, { withSymbol: true })}</Box>
        </Box>
        <Box className={classes.dlItem}>
          <Box component="dt" className={classes.dt}>{t("billing:ledger.drawer.createdAt")}</Box>
          <Box component="dd" className={classes.dd}>{entry.createdAt ? dayjs(entry.createdAt).format("YYYY-MM-DD HH:mm") : t("common:list.dash")}</Box>
        </Box>
        {(entry.reversedBy || entry.reversalOf) && (
          <Box className={classes.dlItem}>
            <Box component="dt" className={classes.dt}>{t("billing:ledger.jrn.noteReversal")}</Box>
            <Box component="dd" className={classes.dd}>
              {entry.reversedBy && (
                <Box component="button" type="button" className={classes.linkBtn} onClick={() => onNavigate(entry.reversedBy!.id)}>
                  {t("billing:ledger.drawer.reversedBy", { number: entry.reversedBy.entryNumber })}
                </Box>
              )}
              {entry.reversalOf && (
                <Box component="button" type="button" className={classes.linkBtn} onClick={() => onNavigate(entry.reversalOf!.id)}>
                  {t("billing:ledger.drawer.reversalOf", { number: entry.reversalOf.entryNumber })}
                </Box>
              )}
            </Box>
          </Box>
        )}
        {entry.alert && (
          <Box className={`${classes.dlItem} ${classes.dlWide}`}>
            <Box className={classes.alertRow} role="alert">
              <WarningCircleIcon size={16} weight="bold" />
              <Text span size="sm">{t("billing:ledger.drawer.alert")}</Text>
            </Box>
          </Box>
        )}
      </Box>
    </Stack>
  );
}

export function EntryLinesCard({
  entry,
  rangeHint,
}: {
  entry: IJournalEntryDetail;
  rangeHint: [string | null, string | null];
}) {
  const { t } = useTranslation(["billing", "common"]);
  const balanced = Number(entry.totalDebit) === Number(entry.totalCredit);
  const diff = Math.abs(Number(entry.totalDebit) - Number(entry.totalCredit));

  // Дансны хуулга огнооны хязгаартай — жагсаалтынх, байхгүй бол бичилтийн сар.
  const from = rangeHint[0] ?? dayjs(entry.entryDate).startOf("month").format("YYYY-MM-DD");
  const to = rangeHint[1] ?? dayjs(entry.entryDate).endOf("month").format("YYYY-MM-DD");
  const statementHref = (accountId: string) =>
    `/ledger/reports/account-statement?accountId=${accountId}&dateFrom=${from}&dateTo=${to}`;

  return (
    <Box className={classes.linesCard}>
      <Box className={classes.linesHead}>
        <Text component="h2" size="sm" fw={600} m={0}>
          {t("billing:ledger.drawer.linesTitle", { count: entry.lines.length })}
        </Text>
        {balanced ? (
          <Text span className={classes.badgeOk}>
            <CheckCircleIcon size={12} weight="bold" className={classes.badgeIcon} />
            {t("billing:ledger.drawer.balanced")}
          </Text>
        ) : (
          <Text span className={classes.badgeBad}>{t("billing:ledger.drawer.unbalanced")}</Text>
        )}
      </Box>

      {!balanced && (
        <Alert color="red" radius="md" m="sm" icon={<WarningIcon size={16} weight="bold" />}>
          {t("billing:ledger.drawer.unbalancedDetail", {
            debit: currencyFormat(entry.totalDebit),
            credit: currencyFormat(entry.totalCredit),
            diff: currencyFormat(diff),
          })}
        </Alert>
      )}

      <Table className={classes.linesTable} data-density="compact" withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("billing:ledger.colAccount")}</Table.Th>
            <Table.Th className={classes.num}>{t("billing:ledger.colDebit")}</Table.Th>
            <Table.Th className={classes.num}>{t("billing:ledger.colCredit")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {entry.lines.map((line) => (
            <Table.Tr key={line.id}>
              <Table.Td>
                <Box className={classes.stack}>
                  <Text span className={classes.accName}>{line.accountName}</Text>
                  <Tooltip label={t("billing:ledger.drawer.accountStatement")} withArrow>
                    <Link className={`${classes.accCode} ${classes.accLink}`} to={statementHref(line.accountId)}>
                      {line.accountCode}
                    </Link>
                  </Tooltip>
                  {line.description?.trim() && <Text span className={classes.sub}>{line.description}</Text>}
                </Box>
              </Table.Td>
              <Table.Td className={`${classes.num} ${line.isDebit ? "" : classes.numEmpty}`}>
                {line.isDebit ? money(line.amount) : t("common:list.dash")}
              </Table.Td>
              <Table.Td className={`${classes.num} ${line.isDebit ? classes.numEmpty : ""}`}>
                {line.isDebit ? t("common:list.dash") : money(line.amount)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Td>{t("billing:ledger.total")}</Table.Td>
            <Table.Td className={classes.num}>{money(entry.totalDebit)}</Table.Td>
            <Table.Td className={classes.num}>{money(entry.totalCredit)}</Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Box>
  );
}

function DrawerSkeleton() {
  return (
    <Stack gap="md" p="lg">
      <Skeleton h={28} w="60%" />
      <Skeleton h={16} w="40%" />
      <Skeleton h={96} />
      <Skeleton h={160} />
    </Stack>
  );
}

/**
 * Hierarchy:
 * JournalEntryDrawer
 * ├── EntryHeader (JE · хуулах · төлөв · ‹ › · хаах)
 * ├── EntrySummary (dl)
 * ├── EntryLinesCard
 * └── footer (Хаах · Урвуу бичилт хийх)
 */
export function JournalEntryDrawer({
  entryId,
  orderedIds,
  rangeHint,
  suspendKeys,
  onClose,
  onNavigate,
  onRequestReverse,
}: Props) {
  const { t } = useTranslation(["billing", "common"]);
  const canFinance = usePermission("FINANCE").hasAccess;
  const isNarrow = useMediaQuery("(max-width: 48em)");
  const opened = entryId !== null;

  const { data: entry, error, isLoading, mutate } = useSwr<IJournalEntryDetail>(
    entryId ? `ledger.entry.${entryId}` : null,
    async () => await LedgerApi.getEntry(entryId!),
    { revalidateOnFocus: false },
  );

  // Only render when SWR payload matches the open id (no stale previous entry).
  const displayEntry = entry && entryId && entry.id === entryId ? entry : null;

  const index = entryId ? orderedIds.indexOf(entryId) : -1;
  const prevId = index > 0 ? orderedIds[index - 1] : null;
  const nextId = index >= 0 && index < orderedIds.length - 1 ? orderedIds[index + 1] : null;

  useEffect(() => {
    if (!opened || suspendKeys) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.altKey || ev.ctrlKey || ev.metaKey || isTyping(document.activeElement)) return;
      if (ev.key === "ArrowLeft" && prevId) {
        ev.preventDefault();
        onNavigate(prevId);
      } else if (ev.key === "ArrowRight" && nextId) {
        ev.preventDefault();
        onNavigate(nextId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened, suspendKeys, prevId, nextId, onNavigate]);

  // Зөвхөн POSTED, урвуулаагүй, мөчлөг нээлттэй үед. Үгүй бол шалтгаантай идэвхгүй товч.
  let reverseBlocked: string | null = null;
  if (displayEntry) {
    if (displayEntry.status === "REVERSED" || displayEntry.reversedBy) {
      reverseBlocked = t("billing:ledger.drawer.reverseBlockedReversed");
    } else if (!displayEntry.periodOpen) {
      reverseBlocked = t("billing:ledger.drawer.reverseBlockedPeriod");
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isNarrow ? "100%" : 600}
      withCloseButton={false}
      classNames={{
        body: classes.drawerBody,
        content: classes.drawerContent,
        header: classes.drawerChromeHeader,
      }}
      title={null}
    >
      {isLoading && !displayEntry && <DrawerSkeleton />}

      {error && !displayEntry && (
        <Stack gap="md" p="lg">
          <Alert color="red" radius="md" icon={<WarningIcon size={16} weight="bold" />}>
            {(error as Error).message || t("billing:ledger.drawer.loadError")}
          </Alert>
          <Button variant="default" size="sm" onClick={() => mutate()}>
            {t("billing:ledger.drawer.retry")}
          </Button>
        </Stack>
      )}

      {displayEntry && (
        <Box className={classes.drawerInner}>
          <Box className={classes.drawerScroll}>
            <EntryHeader
              entry={displayEntry}
              onClose={onClose}
              onPrev={prevId ? () => onNavigate(prevId) : null}
              onNext={nextId ? () => onNavigate(nextId) : null}
            />
            <Stack gap="lg" mt="lg">
              <EntrySummary entry={displayEntry} onNavigate={onNavigate} />
              <EntryLinesCard entry={displayEntry} rangeHint={rangeHint} />
            </Stack>
          </Box>

          <Box className={classes.drawerFooter}>
            <Button variant="default" onClick={onClose}>
              {t("common:close")}
            </Button>
            {canFinance && (
              <Tooltip label={reverseBlocked ?? ""} withArrow disabled={!reverseBlocked}>
                <Box component="span">
                  <Button
                    variant="outline"
                    color="red"
                    leftSection={<ArrowUUpLeftIcon size={14} />}
                    onClick={onRequestReverse}
                    disabled={!!reverseBlocked}
                  >
                    {t("billing:ledger.reverse")}
                  </Button>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

export function ReverseConfirmModal({
  opened,
  onClose,
  onConfirm,
  busy,
  entry,
}: {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  busy: boolean;
  entry: IJournalEntryDetail | IJournalEntry | null | undefined;
}) {
  const { t } = useTranslation(["billing", "common"]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="md"
      centered
      classNames={{ body: modalChrome.modalBodyFlush }}
    >
      <Stack gap="md" align="center" px={24} py={20}>
        <ThemeIcon size={56} radius="xl" color="red" variant="light">
          <WarningIcon size={28} weight="fill" />
        </ThemeIcon>
        <Text fw={700} size="lg" ta="center">
          {t("billing:ledger.reverseConfirmTitle")}
        </Text>

        <Card withBorder radius="md" padding="sm" w="100%" className={classes.reverseSummaryCard}>
          <Stack gap={6}>
            <Group justify="space-between" wrap="nowrap" gap="md">
              <Text size="sm" c="dimmed">
                {t("billing:ledger.colNumber")}
              </Text>
              <Text size="sm" fw={600} ff="monospace">
                {entry?.entryNumber ?? t("common:list.dash")}
              </Text>
            </Group>
            <Group justify="space-between" wrap="nowrap" gap="md">
              <Text size="sm" c="dimmed">
                {t("billing:ledger.colDate")}
              </Text>
              <Text size="sm" fw={600}>
                {entry?.entryDate ? dateFormat(entry.entryDate) : t("common:list.dash")}
              </Text>
            </Group>
            <Group justify="space-between" wrap="nowrap" gap="md">
              <Text size="sm" c="dimmed">
                {t("billing:ledger.colAmount")}
              </Text>
              <Text size="sm" fw={600}>
                {entry ? currencyFormat(entry.totalDebit) : t("common:list.dash")}
              </Text>
            </Group>
            <Text size="sm" c="dimmed" mt={4}>
              {t("billing:ledger.drawer.reverseSummaryNote")}
            </Text>
          </Stack>
        </Card>
      </Stack>
      <ModalFooter>
        <Button variant="default" onClick={onClose} disabled={busy}>
          {t("common:cancel")}
        </Button>
        <Button color="red" onClick={onConfirm} loading={busy}>
          {t("billing:ledger.reverse")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
